export const CUSTOM_SKU_OPTION = "__CUSTOM__";

export const SKU_SERVICE_OPTIONS = [
  { label: "Eterno", value: "ET" },
  { label: "FuneralCloud", value: "FU" },
  { label: "Giottino", value: "GTN" },
  { label: "Add-On", value: "ADD" },
  { label: "Altro", value: CUSTOM_SKU_OPTION },
];

export const SKU_TYPE_OPTIONS = [
  { label: "Standard", value: "ST" },
  { label: "Integrazione", value: "IN" },
  { label: "Altro", value: CUSTOM_SKU_OPTION },
];

const COMBINING_MARKS_RE = /[\u0300-\u036f]/g;
const NON_ALNUM_RE = /[^A-Z0-9]+/g;
const NON_ALNUM_OR_HYPHEN_RE = /[^A-Z0-9-]+/g;
const MULTI_HYPHEN_RE = /-{2,}/g;
const TRIM_HYPHENS_RE = /^-+|-+$/g;
const SKU_CODE_RE = /^[A-Z0-9]{2,5}$/;
const MODULE_SEGMENT_RE = /^[A-Z0-9]+(?:-[A-Z0-9]+)*$/;
const SKU_DATE_RE = /^\d{2}-\d{2}-\d{2}$/;

const toAsciiUpper = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(COMBINING_MARKS_RE, "")
    .toUpperCase();

export const normalizeComparableSku = (value = "") => toAsciiUpper(value).trim();

export const sanitizeSkuCode = (value = "", maxLength = 5) =>
  toAsciiUpper(value).replace(/[^A-Z0-9]/g, "").slice(0, maxLength);

export const sanitizeSkuSegment = (value = "") =>
  toAsciiUpper(value)
    .replace(NON_ALNUM_RE, "-")
    .replace(MULTI_HYPHEN_RE, "-")
    .replace(TRIM_HYPHENS_RE, "");

export const normalizeInlineSku = (value = "") =>
  toAsciiUpper(value)
    .trim()
    .replace(/\s+/g, "-")
    .replace(NON_ALNUM_OR_HYPHEN_RE, "-")
    .replace(MULTI_HYPHEN_RE, "-")
    .replace(TRIM_HYPHENS_RE, "");

export const normalizeSkuDateInput = (value = "") => {
  const digits = String(value).replace(/\D/g, "").slice(0, 6);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

export const formatTodaySkuDate = (date = new Date()) =>
  `${String(date.getFullYear()).slice(-2)}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;

export const isValidSkuDate = (value = "") => {
  if (!SKU_DATE_RE.test(value)) {
    return false;
  }

  const [yy, mm, dd] = value.split("-").map(Number);
  const candidate = new Date(2000 + yy, mm - 1, dd);

  return (
    candidate.getFullYear() === 2000 + yy &&
    candidate.getMonth() === mm - 1 &&
    candidate.getDate() === dd
  );
};

export const isValidSkuCode = (value = "") => SKU_CODE_RE.test(sanitizeSkuCode(value));

export const isValidModuleSegment = (value = "") =>
  !value || MODULE_SEGMENT_RE.test(sanitizeSkuSegment(value));

export const buildSku = ({
  serviceCode,
  moduleSegment,
  typeCode,
  dateSuffix,
}) => {
  const service = sanitizeSkuCode(serviceCode);
  const moduleCode = sanitizeSkuSegment(moduleSegment);
  const type = sanitizeSkuCode(typeCode);
  const date = normalizeSkuDateInput(dateSuffix);

  if (!service || !type || !isValidSkuDate(date)) {
    return "";
  }

  const parts = [service];

  if (moduleCode) {
    parts.push(moduleCode);
  }

  parts.push(type, date);
  return parts.join("-");
};

export const parseSku = (value = "") => {
  const normalized = normalizeInlineSku(value);

  if (!normalized) {
    return { isValid: false, normalized: "" };
  }

  const tokens = normalized.split("-").filter(Boolean);

  if (tokens.length < 5) {
    return { isValid: false, normalized };
  }

  const serviceCode = sanitizeSkuCode(tokens[0]);
  const typeCode = sanitizeSkuCode(tokens[tokens.length - 4]);
  const moduleTokens = tokens.slice(1, -4);
  const moduleSegment = moduleTokens.join("-");
  const dateSuffix = tokens.slice(-3).join("-");
  const rebuilt = buildSku({
    serviceCode,
    moduleSegment,
    typeCode,
    dateSuffix,
  });

  if (
    !rebuilt ||
    rebuilt !== normalized ||
    !isValidSkuCode(serviceCode) ||
    !isValidSkuCode(typeCode) ||
    !isValidModuleSegment(moduleSegment)
  ) {
    return { isValid: false, normalized };
  }

  return {
    isValid: true,
    normalized,
    serviceCode,
    moduleSegment,
    typeCode,
    dateSuffix,
  };
};

export const isValidSkuFormat = (value = "") => parseSku(value).isValid;

export const hasSkuChanged = (nextValue = "", initialValue = "") =>
  normalizeComparableSku(nextValue) !== normalizeComparableSku(initialValue);

export const findDuplicateSku = (
  value = "",
  existingModules = [],
  currentModuleId
) => {
  const candidate = normalizeInlineSku(value);

  if (!candidate) {
    return null;
  }

  return (
    existingModules.find(
      (module) =>
        module?.objectId !== currentModuleId &&
        normalizeInlineSku(module?.sku || "") === candidate
    ) || null
  );
};
